import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useImperativeHandle, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Box, PasswordField, Typography, useMediaQueries } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GoogleButton from '~/components/GoogleButton/GoogleButton';
import IntuitButton from '~/components/IntuitButton/IntuitButton';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useOnMount } from '~/hooks/useOnMount';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useSetAuthToken } from '~/hooks/useSetAuthToken';
import { useLocaleKeys } from '~/i18n';
import XeroSignInButton from '~/pages/unAuthorizedPages/components/XeroSignInButton/XeroSignInButton';
import { selectors } from '~/store/state-selectors';
import { passwordPolicyTextsPath, validatePassword } from '~/utils/password-policy';
import styles from './SecureLoginStep.module.scss';
const SECURE_LOGIN_SET_FLAG = 'secure-login-set-success';
const markSecureLoginSet = ()=>{
    try {
        window.localStorage.setItem(SECURE_LOGIN_SET_FLAG, 'true');
    } catch (unused) {
    /* */ }
};
export const GetSecureLoginStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const SecureLoginStep = (param)=>{
    let { testId, onContinue, onSubmittingChange, submitRef } = param;
    const testIds = useMemo(()=>GetSecureLoginStepTID(testId), [
        testId
    ]);
    const { t: secureLoginStepTexts } = useLocaleKeys('vendorOnboarding.secureLoginStep');
    const { t: pwValidationTexts } = useLocaleKeys(passwordPolicyTextsPath);
    const { api, logBI, fetchUserInfo } = useStoreActions();
    const { reportToHeap } = useEventReporter();
    const setAuthToken = useSetAuthToken();
    const { mobile: isMobile } = useMediaQueries();
    const firstName = useStore(selectors.user.firstName);
    const lastName = useStore(selectors.user.lastName);
    const email = useStore(selectors.user.email);
    const ensureEmail = email !== null && email !== void 0 ? email : undefined;
    useOnMount(()=>{
        logBI(18901);
    });
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState();
    const [confirmError, setConfirmError] = useState();
    const [submitError, setSubmitError] = useState();
    const userInputs = useMemo(()=>[
            email,
            firstName,
            lastName
        ].filter((v)=>Boolean(v)), [
        email,
        firstName,
        lastName
    ]);
    const strengthScore = usePasswordStrength(password, userInputs);
    const handleSubmit = useCallback(async ()=>{
        setSubmitError(undefined);
        const passwordValidation = validatePassword(password, userInputs, pwValidationTexts);
        if (passwordValidation !== true) {
            setPasswordError(passwordValidation);
            return;
        }
        setPasswordError(undefined);
        if (password !== confirmPassword) {
            setConfirmError(secureLoginStepTexts.passwordsDoNotMatch());
            return;
        }
        setConfirmError(undefined);
        onSubmittingChange === null || onSubmittingChange === void 0 ? void 0 : onSubmittingChange(true);
        try {
            logBI(18102);
            logBI(18902);
            reportToHeap('secure-login', 'click', 'client-click-set-login-password');
            const { authToken } = await api.auth.setPassword(password);
            if (authToken != null) {
                await setAuthToken(authToken);
            }
            await fetchUserInfo().catch(()=>undefined);
            markSecureLoginSet();
            onContinue();
        } catch (unused) {
            setSubmitError(secureLoginStepTexts.saveError());
        } finally{
            onSubmittingChange === null || onSubmittingChange === void 0 ? void 0 : onSubmittingChange(false);
        }
    }, [
        api,
        confirmPassword,
        logBI,
        onContinue,
        onSubmittingChange,
        password,
        reportToHeap,
        userInputs
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: handleSubmit
        }), [
        handleSubmit
    ]);
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.root,
        testId: testIds.SecureLoginStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(PasswordField, {
                bi: {
                    name: 'vendor_onboarding_password'
                },
                value: password,
                onChange: (e)=>{
                    setPassword(e.target.value);
                    setPasswordError(undefined);
                    setSubmitError(undefined);
                },
                label: secureLoginStepTexts.passwordLabel(),
                showStrengthMeter: true,
                strengthScore: strengthScore,
                error: passwordError != null,
                helperText: passwordError,
                testId: testIds.SecureLoginStepTestIds.PasswordField()
            }),
            /*#__PURE__*/ _jsx(PasswordField, {
                bi: {
                    name: 'vendor_onboarding_confirm_password'
                },
                value: confirmPassword,
                onChange: (e)=>{
                    setConfirmPassword(e.target.value);
                    setConfirmError(undefined);
                    setSubmitError(undefined);
                },
                label: secureLoginStepTexts.confirmPasswordLabel(),
                error: confirmError != null,
                helperText: confirmError,
                testId: testIds.SecureLoginStepTestIds.ConfirmPasswordField()
            }),
            submitError && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                skin: "error",
                testId: testIds.SecureLoginStepTestIds.ErrorMessage(),
                children: submitError
            }),
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.divider,
                testId: testIds.SecureLoginStepTestIds.OrDivider(),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.dividerLine
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        className: styles.dividerLabel,
                        children: secureLoginStepTexts.orDivider()
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.dividerLine
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Box, {
                className: clsx(styles.oauthRow, isMobile && styles.mobile),
                testId: testIds.SecureLoginStepTestIds.OauthButtonRow(),
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.oauthCell,
                        children: /*#__PURE__*/ _jsx(GoogleButton, {
                            ensureEmail: ensureEmail,
                            width: isMobile ? 320 : 254,
                            onAuthSuccess: ()=>{
                                logBI(18903);
                                markSecureLoginSet();
                                onContinue();
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.oauthCell,
                        children: /*#__PURE__*/ _jsx(XeroSignInButton, {
                            mode: "signIn",
                            ensureEmail: ensureEmail,
                            width: isMobile ? 320 : 254,
                            returnTo: routes.vendorOnboarding
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Box, {
                className: styles.intuitContainer,
                testId: testIds.SecureLoginStepTestIds.IntuitButtonContainer(),
                children: /*#__PURE__*/ _jsx(IntuitButton, {
                    mode: "signIn",
                    ensureEmail: ensureEmail,
                    width: isMobile ? 320 : '100%',
                    returnTo: routes.vendorOnboarding
                })
            })
        ]
    });
};
